import * as React from 'react';
import ReactApexChart from 'react-apexcharts';
import { useTheme } from '@mui/material/styles';
import { Card, CardContent, Typography, Box } from '@mui/material';

export default function SessionsChart({ chargingiInsightData }) {
  const theme = useTheme();

  const { series, options, totalCount, dateRange } = React.useMemo(() => {
    const sourceData = chargingiInsightData?.data?.charging_session;
    
    const rawData = Array.isArray(sourceData)
      ? sourceData
      : (sourceData ? Object.values(sourceData) : []);

    // EFFICIENCY IMPROVEMENT: 
    // We map to timestamps ONCE to avoid repeated 'new Date()' constructor calls during sort/reduce/map.
    const processedRaw = rawData
      .filter(item => item?.date)
      .map(item => ({
        ...item,
        _ts: new Date(item.date).getTime(), // Cache numeric timestamp
        _val: Number(item.totalSessions) || 0 // Cache number conversion
      }));

    // Sort using cached numeric timestamp
    processedRaw.sort((a, b) => a._ts - b._ts);

    let total = 0;
    const chartData = new Array(processedRaw.length);

    // EFFICIENCY IMPROVEMENT:
    // Single loop to calculate Total and build ChartData simultaneously
    for (let i = 0; i < processedRaw.length; i++) {
      const d = processedRaw[i];
      total += d._val;
      
      chartData[i] = {
        x: new Date(d._ts).setHours(0, 0, 0, 0),
        y: d._val,
        breakdown: Array.isArray(d.breakdown) ? d.breakdown : [],
      };
    }

    let range = 'No Data';
    if (chartData.length > 0) {
      const start = new Date(chartData[0].x).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const end = new Date(chartData[chartData.length - 1].x).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      range = `${start} - ${end}`;
    }

    return {
      totalCount: total,
      dateRange: range,
      series: [{ name: 'Sessions', data: chartData }],
      options: {
        chart: { 
          type: 'line', 
          toolbar: { show: false }, 
          zoom: { enabled: false },
          parentHeightOffset: 0,
          sparkline: { enabled: false },
        },
        colors: ['#2e7d32'],
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 4, strokeWidth: 2, hover: { size: 6 } },
        grid: { 
          show: true,
          borderColor: theme.palette.divider,
          padding: { 
            top: 10,
            right: 10,
            bottom: -15, 
            left: 10 
          } 
        },
        xaxis: {
          type: 'datetime',
          axisBorder: { show: true, color: theme.palette.divider },
          axisTicks: { show: true },
          labels: {
            datetimeUTC: false,
            rotate: -45,
            rotateAlways: true,
            offsetY: 0, 
            style: { colors: theme.palette.text.secondary, fontSize: '10px' },
            formatter: (v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          }
        },
        yaxis: {
          axisBorder: { show: true, color: theme.palette.divider },
          labels: { 
            style: { colors: theme.palette.text.secondary },
            formatter: (val) => Math.floor(val) 
          }
        },
        tooltip: {
          enabled: true,
          shared: false,
          intersect: true,
          followCursor: true, 
          theme: theme.palette.mode,
          style: { fontSize: '12px' },
          onDatasetHover: { highlightDataSeries: true },
          fixed: {
            enabled: false,
          },
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const p = w.config.series[seriesIndex].data[dataPointIndex];
            // EFFICIENCY IMPROVEMENT: Use string templates more cleanly
            let items = '';
            const breakdown = p.breakdown;
            for (let i = 0; i < breakdown.length; i++) {
              const b = breakdown[i];
              const vin = b.vin?.trim() || "No VIN";
              const evse = b.evse_id?.trim() || "Unknown EVSE";
              items += `
              <div style="display:flex; justify-content:space-between; gap:20px; font-size:11px; margin-top:5px; border-top:1px solid #eee; padding-top:4px;">
                <span><b>${evse}</b><br/><small>${vin}</small></span>
                <span style="font-weight:700;">${b.count}</span>
              </div>`;
            }

            return `
              <div style="padding:10px; background:${theme.palette.background.paper}; border:1px solid ${theme.palette.divider}; border-radius:4px; box-shadow:${theme.shadows[10]}; pointer-events:none;">
                <div style="font-weight:700; font-size:12px; margin-bottom:4px;">
                  ${new Date(p.x).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
                <div style="color:#2e7d32; font-weight:700; margin-bottom:5px;">Total: ${p.y}</div>
                ${items}
              </div>`;
          }
        }
      }
    };
  }, [chargingiInsightData, theme.palette.divider, theme.palette.text.secondary, theme.palette.mode, theme.palette.background.paper, theme.shadows]); // EFFICIENCY: Specific dependency tracking

  return (
    <Card variant="outlined" sx={{ width: '350', minHeight: 345, borderRadius: 2, overflow: 'visible' }}>
      <CardContent sx={{ overflow: 'visible' }}>
        <Typography variant="subtitle2" fontWeight="bold"  gutterBottom>
          Session Insights (30 Days): {dateRange}
        </Typography>
        <Typography variant="h4" component="p"  sx={{ mb: 1 }}>
          Total Session:{totalCount.toLocaleString()}
        </Typography>

        <Box sx={{ width: '100%', height: 250, overflow: 'visible' }}>
          {series[0].data.length > 0 ? (
            <ReactApexChart 
              options={options} 
              series={series} 
              type="line" 
              height={250} 
              width="100%" 
            />
          ) : (
            <Box display="flex" height="100%" alignItems="center" justifyContent="center">
              <Typography variant="body2" color="text.secondary">No data available</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}